import React, {Dispatch, SetStateAction, useEffect, useState} from "react";
import {ErrorMessage, Field, Form, Formik, FormikHelpers} from "formik";
import {number, object} from "yup";
import {getBalance, getCurrentPaymentMethod, getCustomerPrice, payHours} from "../../core/_requests";
import PaymentForm from "./PaymentForm";
import {useQuery} from "react-query";
import {KTIcon} from "../../../../../_metronic/helpers";
import {Appearance, loadStripe} from "@stripe/stripe-js";
import { Elements } from '@stripe/react-stripe-js';
import {useDispatch, useSelector} from "react-redux";
import {
    setAddingHourFrom,
    setPaymentIsSuccessful,
    setHourToAdd,
    setInsufficientHour,
    setPayableQuantity,
    setCurrentPaymentMethod
} from "../../../../store/AccountSlice";
import {setAutoApproveProject, setAutoPayProject} from "../../../../store/ProjectSlice";
import Swal from "sweetalert2";
import { Modal } from "bootstrap";
import PaymentFormAppearance from "./PaymentFormAppearance";

const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
if (!STRIPE_KEY) {
    throw new Error('Stripe public key is not defined');
  }
const stripePromise = loadStripe(STRIPE_KEY);

const appearance: Appearance={
    theme:'stripe',
    variables:PaymentFormAppearance.variables,
    rules: {
        '.Input, .Block': {
            backgroundColor: 'transparent',
            border: '1.5px solid var(--colorPrimary)',
        },
    },
}

type FormValues = {
    custom_quantity: number,
}

type QuantitySelectionProps = {
    values: FormValues,
    setFieldValue: any,
    setQuantityToPay: Dispatch<SetStateAction<number | undefined>>,
}

const QuantitySelection = ({ values, setFieldValue, setQuantityToPay }: QuantitySelectionProps) => {
    const hourToAdd = useSelector((state: any) => state.account.hourToAdd);

    useEffect(() => {
        if (hourToAdd > 0) {
            setFieldValue('custom_quantity', hourToAdd)
        }
    }, [hourToAdd]);

    useEffect(() => {
        setQuantityToPay(values.custom_quantity);
    }, [values.custom_quantity]);

    return (
        <>
            <div className='fv-row mb-10 mt-5'>
                <label className='form-label'>Needed Hours:</label>
                <Field type={'number'} min={.01} step={.01} name='custom_quantity' placeholder={'Enter custom quantity'} className='form-control form-control-lg form-control-solid' />
                <div className='text-danger mt-2'>
                    <ErrorMessage name='custom_quantity' />
                </div>
            </div>
        </>
    );
};

export default () => {
    const dispatch= useDispatch();
    const addingHourFrom = useSelector((state: any) => state.account.addingHourFrom);
    const insufficientHour = useSelector((state: any) => state.account.insufficientHour);
    const paymentIsSuccessful = useSelector((state: any) => state.account.paymentIsSuccessful);
    const [quantityToPay, setQuantityToPay] = useState<number>();
    const {isLoading: priceIsLoading, data: price, error: priceError} = useQuery(
        '/price/basic',
        () => getCustomerPrice()
    )
    const [clientSecret, setClientSecret] = useState<string>();
    const [showPaymentForm, setShowPaymentForm] = useState<boolean>(false);
    const [paymentError, setPaymentError] = useState<boolean>(false);
    const {data: balance} = useQuery(
        '/wallet/balance',
        () => getBalance()
    );
    const isNewPaymentMethod= useSelector((state: any) => state.account.isNewPaymentMethod);
    const paymentMethod = useSelector((state: any) => state.account.currentPaymentMethod);
   // const [paymentMethod,setPaymentMethod]=useState<PaymentMethod | null>(null);
    
    useEffect(()=>{
        getCurrentPaymentMethod().then((response)=>{
            if(response){
                //setPaymentMethod(response);
                dispatch(setCurrentPaymentMethod(response));
                        }
        })
    },[]);

    useEffect(()=>{
        getCurrentPaymentMethod().then((response)=>{
            if(response){
                dispatch(setCurrentPaymentMethod(response));
            }
        })
    },[isNewPaymentMethod]);
    

    const onSubmit = ({ custom_quantity }: FormValues, { setSubmitting, resetForm }: FormikHelpers<FormValues>) => {
        setPaymentError(false);

        if(paymentMethod!=null){
            const modal=document.getElementById('payment_methods');
            const approve_dismisalBtn=document.getElementById('approveHoursCloseModalID');
            const hours_dismisalBtn=document.getElementById('projectHoursCloseModalID');
            const default_dismisalBtn=document.getElementById('defaultHoursCloseModalID');
            if(modal){
                const paymentModal = new Modal(modal);
                dispatch(setPayableQuantity(custom_quantity));
                setSubmitting(false); 
                approve_dismisalBtn?.click();
                hours_dismisalBtn?.click();
                default_dismisalBtn?.click();
                paymentModal.toggle();
            }
        }
        else{
            payHours(custom_quantity)
            .then((payment) => {
                if (!payment) return;
                dispatch(setInsufficientHour(false));
                if (payment.status === 'succeeded') {
                    handleCloseModal();
                    const modal=document.getElementById('approveCloseModalID'); 
                     modal?.click();  
                    Swal.fire({
                        text: "Success! Thank you for your payment.",
                        icon: "success",
                        buttonsStyling: false,
                        confirmButtonText: "OK",
                        customClass: {
                            confirmButton: "btn btn-primary",
                        }
                    }).then((result)=>{
                        if(result.isConfirmed){
                            dispatch(setAutoApproveProject(true));                     
                        }
                    })
                    resetForm();
                } else {
                    setClientSecret(payment.client_secret);
                    setShowPaymentForm(true);
                }
            })
            .catch(() => {setPaymentError(true);})
            .finally(() => setSubmitting(false));
        }
    }

    const handleCloseModal = () => {
        setShowPaymentForm(false);
        setClientSecret(undefined);
    }

    const handleCloseSuccess = () => {
        if (addingHourFrom == 'approve_project') {
            dispatch(setAutoPayProject(false));
            dispatch(setAutoApproveProject(true));
        } else if (addingHourFrom == 'pay_project') {
            dispatch(setAutoApproveProject(false));
            dispatch(setAutoPayProject(true));
        }
        handleCloseModal();
        dispatch(setAddingHourFrom('default'));
        dispatch(setPaymentIsSuccessful(false));
    }

    return (
        <div className="modal fade" id="modal_add_hour" role="dialog" aria-hidden="true" data-bs-backdrop='static'>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content modal-rounded">
                    <div className="modal-header">
                        <h2>Add Hours</h2>
                        {addingHourFrom == 'project_form' ? (
                            <div className="btn btn-sm btn-icon btn-active-color-primary" id="projectHoursCloseModalID" onClick={handleCloseModal} data-bs-dismiss="modal" data-bs-toggle="modal" data-bs-target="#modal_create_project">
                                <i className="ki-duotone ki-cross fs-1">
                                    <span className="path1"></span>
                                    <span className="path2"></span>
                                </i>
                            </div>
                        ) : (addingHourFrom == 'approve_project' || addingHourFrom == 'pay_project') ? (
                            <div className="btn btn-sm btn-icon btn-active-color-primary" id="approveHoursCloseModalID" onClick={handleCloseModal} data-bs-dismiss="modal">
                                <i className="ki-duotone ki-cross fs-1">
                                    <span className="path1"></span>
                                    <span className="path2"></span>
                                </i>
                            </div>
                        ) : (
                            <div className="btn btn-sm btn-icon btn-active-color-primary" data-bs-dismiss="modal" id="defaultHoursCloseModalID">
                                <i className="ki-duotone ki-cross fs-1">
                                    <span className="path1"></span>
                                    <span className="path2"></span>
                                </i>
                            </div>
                        )}
                    </div>

                    {/*@ts-ignore*/}
                    <div className="modal-body scroll-y m-5">
                        {paymentIsSuccessful ? (
                            <div className={'d-flex flex-column align-items-center'}>
                                <div className="mb-7">
                                    <KTIcon iconName='check-circle' className='fs-5x me-5 text-primary' />
                                </div>
                                <div className="fw-semibold fs-3 mb-7">
                                    Success! Thank you for your payment.
                                </div>
                                <div className="mb-10">
                                    {addingHourFrom == 'project_form' ? (
                                        <button className='btn btn-sm btn-primary' data-bs-dismiss="modal" onClick={() => dispatch(setPaymentIsSuccessful(false))}>
                                            <span className='indicator-label'>Close</span>
                                        </button>
                                    ) : (addingHourFrom == 'approve_project' || addingHourFrom == 'pay_project') ? (
                                        <button className='btn btn-sm btn-primary' data-bs-dismiss="modal" onClick={handleCloseSuccess}>
                                            <span className='indicator-label'>Close</span>
                                        </button>
                                    ) : (
                                        <button className='btn btn-sm btn-primary' onClick={handleCloseSuccess} data-bs-toggle="modal" data-bs-target="#modal_create_project" data-bs-dismiss="modal">
                                            <span className='indicator-label'>Close</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <>
                                {priceIsLoading && (
                                    <div className="text-center">
                                        <span className="spinner-border align-middle text-primary"></span>
                                    </div>
                                )}
                                {priceError && (
                                    <div className="alert alert-danger d-flex align-items-center p-5 mb-10">
                                        <KTIcon iconName='cross' className='fs-2x me-3 svg-icon-danger' />

                                        <div className="d-flex flex-column">
                                            <h5 className="mb-1">Error</h5>
                                            <span>An error occurred while fetching the price. Please try again later.</span>
                                        </div>
                                    </div>
                                )}
                                {insufficientHour && (
                                    <div className="alert alert-warning d-flex align-items-center p-5">
                                        <i className="ki-duotone ki-shield-tick fs-2hx text-warning me-4"><span
                                            className="path1"></span><span className="path2"></span></i>
                                        <div className="d-flex flex-column">
                                            <h4 className="mb-1 text-dark">Insufficient hours</h4>
                                            <p>
                                                You don't have enough hours in your balance to validate or pay for this project.
                                                <br/>
                                                Please purchase more hour.
                                                <br/>
                                                <span className="text-black">Your current balance: {balance} hour</span>
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {!showPaymentForm && price && (
                                    <>
                                        <Formik
                                            onSubmit={onSubmit}
                                            initialValues={{
                                                custom_quantity: 0,
                                            }}
                                            validationSchema={object().shape({
                                                custom_quantity: number().required().min(0.01),
                                            })}
                                        >
                                            {({values, isSubmitting, setFieldValue}) => (
                                                <Form>
                                                    <QuantitySelection values={values} setFieldValue={setFieldValue}
                                                                       setQuantityToPay={setQuantityToPay}/>

                                                    <p className={'text-muted fs-5 mt-2'}>Hour Price = ${price?.unit_amount * 100}/Hour</p>
                                                    <div className='fv-row mb-10 text-center'>
                                                        <button type='submit' className='btn btn-primary'>
                                                            {!isSubmitting && (
                                                                <span className='indicator-label'>
                                                        Pay {quantityToPay && `$${(parseFloat(quantityToPay.toFixed(2)) * price.unit_amount * 100).toFixed(2)}`}
                                                    </span>
                                                            )}
                                                            {(isSubmitting) && (
                                                                <span className='indicator-progress' style={{display: 'block'}}>
                                                        Please wait...
                                                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                                    </span>
                                                            )}
                                                        </button>
                                                    </div>
                                                    {paymentError && (
                                                        <div className="alert alert-danger p-2 mb-5">
                                                            <div className="d-flex flex-column">
                                                                <h5 className="mb-1">Error</h5>
                                                                <span>An error has occurred</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Form>
                                            )}
                                        </Formik>
                                    </>
                                )}
                                {showPaymentForm && (
                                    <div className={'d-flex flex-column text-center'}>
                                        <div className="d-flex align-items-start justify-content-center mb-7">
                                            <span className="fw-bold fs-4 mt-1 me-2">Hours</span>
                                            <span className="fw-bold fs-3x" id="create_project_budget_label">{quantityToPay}</span>
                                            <span className="text-muted fw-semibold fs-5 ms-2 mb-2 align-self-end">{quantityToPay && price && `for $${(parseFloat(quantityToPay.toFixed(2)) * price.unit_amount * 100).toFixed(2)}`}</span>
                                        </div>
                                    </div>
                                )}
                                {clientSecret && (
                                    <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
                                        <PaymentForm setClientSecret={setClientSecret} setVisible={setShowPaymentForm} />
                                    </Elements>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}